import React, { useEffect, useMemo, useState } from 'react';
import { Card, Steps, Space, Button, message, Form, Input, Select, Table, Descriptions, Modal } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { getDebtDetail, getNoticeTemplates } from '../../services/debt';
import { DebtTypeOptions, labelOfDebtType, labelOfDebtStatus, toSelectOptions } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';


const KEY = 'swy_publish_notice_state';
const loadState = () => {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (_) { return {}; }
};
const saveState = (next) => {
  const prev = loadState();
  sessionStorage.setItem(KEY, JSON.stringify({ ...prev, ...next }));
};

/**
 * 催告发布步骤2：选择模板
 * 展示与筛选催告模板，选择后保存预览信息并进入内容编辑。
 */
const PublishNoticeChooseTemplatePage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [contentHtml, setContentHtml] = useState(loadState().contentHtml || '');
  const [templateType, setTemplateType] = useState(loadState().templateType || null);
  const [templateName, setTemplateName] = useState(loadState().templateName || null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(loadState().selectedTemplateId || null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const [sorter, setSorter] = useState({ field: 'update_time', order: 'desc' });
  const [filterForm] = Form.useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewHtmlModal, setPreviewHtmlModal] = useState('');

  const templateTypeLabel = (code) => {
    const c = String(code || '').toUpperCase();
    if (c === 'HTML') return 'HTML';
    if (c === 'DOCX') return 'DOCX';
    return c || '';
  };

  const fetchTemplates = async (pageCfg = pagination, sortCfg = sorter) => {
    try {
      setLoading(true);
      const values = filterForm.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        keyword: values.template_name,
        model_type: values.business_type,
        sort_field: sortCfg.field,
        sort_order: sortCfg.order,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getNoticeTemplates(params);
      const list = Array.isArray(data?.records) ? data.records : Array.isArray(data?.list) ? data.list : [];
      setRecords(list);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const state = loadState();
    if (!state.selectedDebtId && !id) {
      navigate('/debt-notice/publish');
      return;
    }
    const debtId = id || state.selectedDebtId;
    getDebtDetail(debtId).then(({ data }) => setSelectedDebt(data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const prev = () => {
    navigate('/debt-notice/publish');
  };

  const next = () => {
    if (!selectedTemplateId || !templateType || !templateName) {
      message.warning('请选择催告函模板');
      return;
    }
    saveState({ selectedTemplateId, templateType, templateName, contentHtml });
    const state = loadState();
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/edit-content/${debtId}`);
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTemplates({ current: 1, pageSize: pagination.pageSize });
  };

  const handleReset = () => {
    filterForm.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    setSorter({ field: 'update_time', order: 'desc' });
    fetchTemplates({ current: 1, pageSize: pagination.pageSize }, { field: 'update_time', order: 'desc' });
  };

  const handleSelectTemplate = (record) => {
    const idVal = record?.id ?? record?.templateId ?? record?.modelId;
    const nameVal = record?.name ?? record?.templateName ?? record?.modelName;
    const typeVal = templateTypeLabel(record?.file_type ?? record?.fileType ?? record?.type ?? record?.templateType);
    setSelectedTemplateId(idVal);
    setTemplateName(nameVal);
    setTemplateType(typeVal);
    const html = record?.previewHtml ?? record?.contentHtml ?? '';
    setContentHtml(html);
  };

  const openPreview = (record) => {
    const nameVal = record?.name ?? record?.templateName ?? record?.modelName ?? '模板预览';
    const html = record?.previewHtml ?? record?.contentHtml ?? '';
    setPreviewTitle(nameVal);
    setPreviewHtmlModal(html || '<div style="color:#999;">暂无预览内容</div>');
    setPreviewOpen(true);
  };

  const columns = [
    { title: '模板名称', dataIndex: 'name', key: 'name', ellipsis: true, render: (v, r) => v || r.templateName || r.modelName || '' },
    { title: '业务类型', dataIndex: 'model_type', key: 'model_type', width: 160, render: (v, r) => labelOfDebtType(r.model_type || r.modelType || '') },
    { title: '发布时间', dataIndex: 'publishTime', key: 'publishTime', width: 180, sorter: true, render: (v, r) => {
      const t = r.publishTime || r.publish_time;
      return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '';
    } },
    { title: '浏览量', dataIndex: 'views', key: 'views', width: 120 },
    { title: '操作', key: 'action', width: 160, render: (_, record) => (
      <Space>
        <Button type="link" onClick={() => openPreview(record)}>预览</Button>
      </Space>
    ) },
  ];

  return (
    <div className="page-container">
      <div className="page-content page-content-wide">
        <div className="page-title">发布催告</div>

        <Card className="card-compact" size="small">
          {isMobile ? (
            <div className="c-steps-mobile-header" aria-label="步骤信息">
              <div className="c-steps-mobile-left">
                <span className="c-steps-mobile-stepno">第 2 步</span>
                <span className="c-steps-mobile-title">选择模板</span>
              </div>
              <div className="c-steps-mobile-progress">2/7</div>
            </div>
          ) : (
            <Steps
              current={1}
              size="small"
              items={[
                { title: '选择债权' },
                { title: '选择模板' },
                { title: '编辑内容' },
                { title: '签章与预览' },
                { title: '生成催告函' },
                { title: '发送催告' },
                { title: '归档' },
              ]}
            />
          )}
        </Card>

        <div className="mb-md">
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            wrap={!isMobile}
            style={{ width: isMobile ? '100%' : undefined }}
          >
            <Button onClick={prev} block={isMobile}>上一步</Button>
            <Button type="primary" onClick={next} block={isMobile}>下一步</Button>
          </Space>
        </div>

        <Card className="card-compact" size="small">
          {selectedDebt && (
            <Descriptions title="已选债权信息" bordered size="small" column={isMobile ? 1 : 2}>
              <Descriptions.Item label="编号">{selectedDebt?.debt?.debtNo}</Descriptions.Item>
              <Descriptions.Item label="债权名称">{selectedDebt?.debt?.title}</Descriptions.Item>
              <Descriptions.Item label="债权类型">{labelOfDebtType(selectedDebt?.debt?.debtType)}</Descriptions.Item>
              <Descriptions.Item label="发生日">{selectedDebt?.debt?.debtOccurrenceDate ? dayjs(selectedDebt.debt.debtOccurrenceDate).format('YYYY-MM-DD') : ''}</Descriptions.Item>
              <Descriptions.Item label="本金金额">{formatAmountText(selectedDebt?.debt?.principalAmount)}</Descriptions.Item>
              <Descriptions.Item label="合计金额">{formatAmountText(selectedDebt?.debt?.totalAmount)}</Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Card className="card-compact" size="small">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={filterForm} onFinish={handleSearch}>
            <Form.Item name="template_name" label="关键字">
              <Input placeholder="请输入模板名称/说明" allowClear style={{ width: isMobile ? '100%' : 220 }} />
            </Form.Item>
            <Form.Item name="business_type" label="业务类型">
              <Select allowClear style={{ width: isMobile ? '100%' : 220 }} options={toSelectOptions(DebtTypeOptions)} />
            </Form.Item>
            <Form.Item>
              <Space
                direction={isMobile ? 'vertical' : 'horizontal'}
                style={{ width: isMobile ? '100%' : undefined }}
              >
                <Button type="primary" htmlType="submit" block={isMobile}>查询</Button>
                <Button onClick={handleReset} block={isMobile}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact" size="small">
          <Table
            size="small"
            rowKey={(r) => r.id ?? r.templateId ?? r.modelId}
            loading={loading}
            dataSource={records}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: PAGINATION_ENABLE_SIZE_CHANGER,
              pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
            }}
            onChange={(pag, filters, sorterArg) => {
              const order = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
              const field = sorterArg?.field || sorter.field;
              const nextSorter = { field, order };
              setSorter(nextSorter);
              const nextPagination = { current: pag.current, pageSize: normalizePageSize(pag.pageSize), total: pagination.total };
              setPagination(nextPagination);
              fetchTemplates(nextPagination, nextSorter);
            }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedTemplateId ? [selectedTemplateId] : [],
              onChange: (keys) => setSelectedTemplateId(keys[0]),
              onSelect: (record) => handleSelectTemplate(record),
            }}
          />
        </Card>

        <Modal
          title={previewTitle || '模板预览'}
          open={previewOpen}
          onCancel={() => setPreviewOpen(false)}
          footer={null}
          width={isMobile ? '96vw' : 800}
        >
          <div style={{ border:'1px solid #eee', padding:16, minHeight: 120 }} dangerouslySetInnerHTML={{ __html: previewHtmlModal }} />
        </Modal>
      </div>
    </div>
  );
};

export default PublishNoticeChooseTemplatePage;
